import React from 'react';
import { InputNumber } from '@components/framework/input/InputNumber';
import { FlagSetting, ReservationFlagDTO, ReservationFlagTypeEnum } from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import RingilDropdown, { RingilDropdownItem } from '@components/RingilComponents/RingilDropdown';
import { findExceededUnitLimits, UnitCountableOrder, unitLimitErrorMessage } from '@components/RingilComponents/form/flagUnitsLimit';

interface ReservationFlagsFieldProps {
  value: ReservationFlagDTO[];
  onChange: (flags: ReservationFlagDTO[]) => void;
  flagsConfig: Partial<Record<ReservationFlagTypeEnum, FlagSetting>>;
  flagsErrorMessage?: string;
  unitsErrorMessage?: (index: number) => string | undefined;
  /** Orders booked on the reservation; caps the units of cross dock. Omit where they are unknown. */
  orders?: UnitCountableOrder[];
  dataTestIdPrefix?: string;
}

const ReservationFlagsField: React.FC<ReservationFlagsFieldProps> = ({
  value,
  onChange,
  flagsConfig,
  flagsErrorMessage,
  unitsErrorMessage,
  orders,
  dataTestIdPrefix,
}) => {
  const { tr } = useTranslationLgs();

  // Only the capped flags produce a message here; `unitsErrorMessage` from an enclosing form wins so a
  // "required" error is not hidden behind the cap.
  const exceededLimits = React.useMemo(() => findExceededUnitLimits(value, orders), [value, orders]);

  const visibleFlagOptions = Object.values(ReservationFlagTypeEnum)
    .filter(flag => flagsConfig[flag]?.visible)
    .map(flag => ({ value: flag, label: tr(`ReservationFlag.${flag}`, flag) }));

  if (visibleFlagOptions.length === 0) return null;

  const flagsTestId = dataTestIdPrefix ? `${dataTestIdPrefix}-flags` : undefined;
  const unitsTestId = (type: ReservationFlagTypeEnum) =>
    dataTestIdPrefix ? `${dataTestIdPrefix}-flag-${type}-units` : undefined;

  const dropdownItems: RingilDropdownItem[] = visibleFlagOptions.map(opt => ({
    id: opt.value,
    label: opt.label,
    icon: <FlagIcon type={opt.value} className="w-4 h-4" />,
  }));

  return (
    <>
      <Field label={tr('ReservationFlagsField.label', 'Flags')} errorMessage={flagsErrorMessage}>
        <RingilDropdown
          items={dropdownItems}
          selectedIds={value?.map(f => f.reservationFlagType) ?? []}
          onSelectionChange={ids => {
            const existing = value ?? [];
            const next = ids.map(
              id =>
                existing.find(f => f.reservationFlagType === (id as ReservationFlagTypeEnum)) ?? {
                  reservationFlagType: id as ReservationFlagTypeEnum,
                  units: undefined,
                },
            );
            onChange(sortFlags(next));
          }}
          placeholder={tr('ReservationFlagsField.placeholder', 'Select flags')}
          dataTestIdBase={flagsTestId}
          showSelectedIcons
        />
      </Field>
      {sortFlags(value ?? []).map(flag => {
        const cfg = flagsConfig[flag.reservationFlagType];
        if (!cfg?.visibleAdditionalField) return null;
        const originalIdx = (value ?? []).findIndex(f => f.reservationFlagType === flag.reservationFlagType);
        const exceeded = exceededLimits.find(l => l.reservationFlagType === flag.reservationFlagType);
        return (
          <Field
            key={flag.reservationFlagType}
            required={cfg.additionalFieldRequired}
            label={tr(`ReservationFlag.${flag.reservationFlagType}Units`, `ReservationFlag.${flag.reservationFlagType}`)}
            errorMessage={unitsErrorMessage?.(originalIdx) ?? (exceeded && unitLimitErrorMessage(tr, exceeded.maxUnits))}
          >
            <InputNumber
              value={flag.units ?? null}
              onChange={e => {
                const next = (value ?? []).map(f =>
                  f.reservationFlagType === flag.reservationFlagType
                    ? { ...f, units: e.value ?? undefined }
                    : f,
                );
                onChange(next);
              }}
              min={0}
              data-testid={unitsTestId(flag.reservationFlagType)}
            />
          </Field>
        );
      })}
    </>
  );
};

export default ReservationFlagsField;